import { PropsWithChildren, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Clear from "@mui/icons-material/Clear";
import {
  Box,
  Container,
  IconButton,
  inputBaseClasses,
  OutlinedInput,
  Skeleton,
  Stack,
} from "@mui/material";
import useConceptBreadcrumbs from "catalog/hooks/useConceptBreadcrumbs";
import { SearchIcon } from "~/assets/icons";
import { TitleBreadcrumbs } from "~/components/AppHeader";
import PageHeader from "~/components/PageHeader";
import StickyToolbar from "~/components/StickyToolbar";
import useAppHeader from "~/contexts/header";
import { useDocumentTitle } from "~/hooks/useDocumentTitle";

const SearchInput = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [query, setQuery] = useState("");

  return (
    <OutlinedInput
      name="concept-search"
      size="small"
      placeholder="Search"
      onChange={(e) => setQuery(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          navigate(`/data-browser/dashboards/concepts/search?q=${query}`, {
            state: { referrer: location.pathname },
          });
        }
      }}
      value={query}
      sx={{
        minWidth: 250,
        [`& .${inputBaseClasses.input}`]: {
          padding: 1,
        },
      }}
      startAdornment={
        <Box display="flex" alignItems="center" justifyContent="center" pl={0.5}>
          <SearchIcon width={16} />
        </Box>
      }
      endAdornment={
        <Box display="flex" alignItems="center" gap={0.25}>
          {query && (
            <IconButton
              edge="end"
              size="small"
              aria-label="Clear search"
              onMouseDown={(e) => {
                // Prevent input blur (alongside using onMouseDown instead of onClick)
                e.preventDefault();

                setQuery("");
              }}
              sx={{ fontSize: 20, p: 0.25 }}
            >
              <Clear fontSize="inherit" />
            </IconButton>
          )}
        </Box>
      }
    />
  );
};

const BrowseConceptsLayout = ({ children }: PropsWithChildren) => {
  const crumbs = useConceptBreadcrumbs();
  const endCrumb = crumbs.at(-1);
  useDocumentTitle(
    [...crumbs]
      .reverse()
      .map((crumb) => crumb.label)
      .join(" · ")
  );
  useAppHeader({
    title: (
      <TitleBreadcrumbs
        crumbs={[
          { title: "Data Browser", href: "/data-browser" },
          { title: "Dashboards", href: "/data-browser/dashboards" },
          ...crumbs.map((crumb) => ({
            title: crumb.label,
            href: crumb.href ?? "",
          })),
        ]}
      />
    ),
  });

  return (
    <Stack minHeight="100%" bgcolor="common.dimmedPageBackground">
      <StickyToolbar
        sx={{
          py: 1.5,
        }}
      >
        <Container maxWidth="xl">
          <Box display="flex" justifyContent="space-between" alignItems="start" gap={1}>
            <Box display="flex" flexDirection="column" gap={1} flexWrap="wrap" overflow="hidden">
              {endCrumb?.badge && <Box>{endCrumb.badge}</Box>}
              <PageHeader sx={{ my: 0.5 }}>
                {endCrumb?.loading ? <Skeleton width={300} /> : endCrumb?.label}
              </PageHeader>
            </Box>
            <SearchInput />
          </Box>
        </Container>
      </StickyToolbar>
      <Box flex={1} py={2}>
        <Container maxWidth="xl" sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {children}
        </Container>
      </Box>
    </Stack>
  );
};

export default BrowseConceptsLayout;
